import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Container, KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
  ToolbarActions,
} from '@/partials/toolbar';
import { enqueueSnackbar } from 'notistack';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import {
  MemberRosterFormState,
  MemberRosterEntry,
  POSITIONS_OF_TRUST,
  INVOICE_DESTINATIONS,
} from '@/types/members-roster.types';
// Assuming these hooks will be created in this service
import {
  useGetMemberRosterByIdQuery,
  useCreateMemberRosterMutation,
  useUpdateMemberRosterMutation,
} from '@/services/ClubMembersRosterService';

interface MembersRosterFormProps {
  mode: 'create' | 'edit' | 'view';
}

const initialFormState: MemberRosterFormState = {
  federation_membership_number: '',
  club_id: '',
  membership_category: '',
  membership_start_date: '',
  membership_end_date: '',
  svp_membership_id: '',
  position_of_trust: '',
  invoice_destination: 'home_address',
  street_address: '',
  postal_code: '',
  city: '',
  country: '',
  name: '',
  email: '',
  phone: '',
  billing_name: '',
  billing_email: '',
  billing_phone: '',
  honours_awards: '',
  storage_locker_key: '',
  eye_color: '',
  shoe_size: '',
  user_id: '',
};

export const MembersRosterForm = ({ mode }: MembersRosterFormProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const clubId = params.id;
  const rosterId = params.rosterId;
  const isViewMode = mode === 'view';
  const intl = useIntl();
  const { currentLanguage, changeLanguage } = useLanguage();

  const [formState, setFormState] = useState<MemberRosterFormState>({
    ...initialFormState,
    club_id: clubId || '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [useMemberDetailsForBilling, setUseMemberDetailsForBilling] =
    useState(false);

  // API Hooks
  const { data: rosterData, isLoading: isLoadingData } =
    useGetMemberRosterByIdQuery(rosterId!, {
      skip: !rosterId || mode === 'create',
    });

  const [createMemberRoster, { isLoading: isCreating }] =
    useCreateMemberRosterMutation();
  const [updateMemberRoster, { isLoading: isUpdating }] =
    useUpdateMemberRosterMutation();

  const isSubmitting = isCreating || isUpdating;

  const handleCancel = () => {
    navigate(`/club-management/${clubId}`);
  };

  // Get current club name (in real implementation, fetch from API)
  const currentClubName = intl.formatMessage({
    id: 'MEMBERS_ROSTER.MOCK_CLUB_NAME',
  });

  // Membership categories with translations
  const MEMBERSHIP_CATEGORIES = [
    {
      id: 'permanent_member',
      label: intl.formatMessage({
        id: 'MEMBERS_ROSTER.CATEGORY.PERMANENT_MEMBER',
      }),
    },
    {
      id: 'junior_member',
      label: intl.formatMessage({
        id: 'MEMBERS_ROSTER.CATEGORY.JUNIOR_MEMBER',
      }),
    },
    {
      id: 'honorary_member',
      label: intl.formatMessage({
        id: 'MEMBERS_ROSTER.CATEGORY.HONORARY_MEMBER',
      }),
    },
    {
      id: 'invited_member',
      label: intl.formatMessage({
        id: 'MEMBERS_ROSTER.CATEGORY.INVITED_MEMBER',
      }),
    },
  ];

  useEffect(() => {
    if (rosterData && (mode === 'edit' || mode === 'view')) {
      const data = rosterData;
      const formatDate = (dateString: string) => {
        if (!dateString) return '';
        return dateString.split('T')[0]; // Truncate ISO string to YYYY-MM-DD
      };

      setFormState({
        federation_membership_number: data.federation_membership_number || '',
        club_id: clubId || '',
        membership_category: data.membership_category || '',
        membership_start_date: formatDate(data.membership_start_date),
        membership_end_date: formatDate(data.membership_end_date),
        svp_membership_id: data.svp_membership_id || '',
        position_of_trust: data.position_of_trust || '',
        invoice_destination: data.invoice_destination || 'home_address',
        street_address: data.street_address || '',
        postal_code: data.postal_code || '',
        city: data.city || '',
        country: data.country || '',
        name: data.name || data.user?.name || '',
        email: data.email || data.user?.email || '',
        phone: data.phone || data.user?.mobile || '',
        billing_name: data.billing_name || '',
        billing_email: data.billing_email || '',
        billing_phone: data.billing_phone || '',
        honours_awards: data.honours_awards || '',
        storage_locker_key: data.storage_locker_key || '',
        eye_color: data.eye_color || '',
        shoe_size: data.shoe_size || '',
        user_id: data.user_id || '',
      });
    }
  }, [rosterData, mode, clubId, rosterId]);

  const handleUseMemberDetailsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setUseMemberDetailsForBilling(isChecked);

    if (isChecked) {
      setFormState((prev) => ({
        ...prev,
        billing_name: prev.name,
        billing_email: prev.email,
        billing_phone: prev.phone,
      }));
      // Clear billing errors if any
      setErrors((prev) => {
        const next = { ...prev };
        delete next.billing_name;
        delete next.billing_email;
        delete next.billing_phone;
        return next;
      });
    } else {
      // Optional: Clear billing fields when unchecked? Usually better to keep them.
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => {
      const newState = { ...prev, [name]: value };

      // If checkbox is checked and user updates member details, update billing details too
      if (useMemberDetailsForBilling) {
        if (name === 'name') newState.billing_name = value;
        if (name === 'email') newState.billing_email = value;
        if (name === 'phone') newState.billing_phone = value;
      }

      return newState;
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Required fields validation
    const requiredFields = [
      'svp_membership_id',
      'membership_category',
      'invoice_destination',
      'federation_membership_number',
      'membership_start_date',
      'membership_end_date',
      'position_of_trust',
      'street_address',
      'postal_code',
      'city',
      'country',
      'name',
      'email',
      'phone',
    ];

    requiredFields.forEach((field) => {
      if (
        !formState[field] ||
        (typeof formState[field] === 'string' && !formState[field].trim())
      ) {
        newErrors[field] = intl.formatMessage({
          id: `MEMBERS_ROSTER.VALIDATION.${field.toUpperCase()}_REQUIRED`,
          defaultMessage: 'This field is required',
        });
      }
    });

    // Postal code validation (exactly 5 digits)
    if (formState.postal_code && formState.postal_code.trim()) {
      const postalCodeRegex = /^\d{5}$/;
      if (!postalCodeRegex.test(formState.postal_code)) {
        newErrors.postal_code = intl.formatMessage({
          id: 'MEMBERS_ROSTER.VALIDATION.POSTAL_CODE_INVALID',
          defaultMessage: 'Postal code must be exactly 5 digits',
        });
      }
    }

    // Date logic validation
    if (formState.membership_start_date && formState.membership_end_date) {
      const startDate = new Date(formState.membership_start_date);
      const endDate = new Date(formState.membership_end_date);
      if (endDate <= startDate) {
        newErrors.membership_end_date = intl.formatMessage({
          id: 'MEMBERS_ROSTER.VALIDATION.END_DATE_AFTER_START',
        });
      }
    }

    // Format validation for Member details
    if (formState.email && formState.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formState.email)) {
        newErrors.email = intl.formatMessage({
          id: 'MEMBERS_ROSTER.VALIDATION.INVALID_EMAIL',
          defaultMessage: 'Invalid email format',
        });
      }
    }

    if (formState.phone && formState.phone.trim()) {
      const phoneRegex = /^[\d\s+\-()]+$/;
      if (!phoneRegex.test(formState.phone)) {
        newErrors.phone = intl.formatMessage({
          id: 'MEMBERS_ROSTER.VALIDATION.INVALID_PHONE',
          defaultMessage: 'Invalid phone number format',
        });
      }
    }

    // Optional fields format validation
    if (formState.billing_email && formState.billing_email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formState.billing_email)) {
        newErrors.billing_email = intl.formatMessage({
          id: 'MEMBERS_ROSTER.VALIDATION.INVALID_EMAIL',
          defaultMessage: 'Invalid email format',
        });
      }
    }

    if (formState.billing_phone && formState.billing_phone.trim()) {
      // Allow numbers, spaces, +, -, (, )
      const phoneRegex = /^[\d\s+\-()]+$/;
      if (!phoneRegex.test(formState.billing_phone)) {
        newErrors.billing_phone = intl.formatMessage({
          id: 'MEMBERS_ROSTER.VALIDATION.INVALID_PHONE',
          defaultMessage: 'Invalid phone number format',
        });
      }
    }

    if (formState.shoe_size && formState.shoe_size.trim()) {
      const shoeSizeRegex = /^\d+(\.\d+)?$/;
      if (!shoeSizeRegex.test(formState.shoe_size)) {
        newErrors.shoe_size = intl.formatMessage({
          id: 'MEMBERS_ROSTER.VALIDATION.INVALID_SHOE_SIZE',
          defaultMessage: 'Shoe size must be a number',
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (isViewMode) {
      navigate(`/club-management/${clubId}`);
      return;
    }

    if (!validate()) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'MEMBERS_ROSTER.VALIDATION.FIX_ERRORS' }),
        { variant: 'error' }
      );
      return;
    }

    try {
      // Prepare payload, excluding user_id if empty
      const { user_id, ...rest } = formState;
      const payload: Partial<MemberRosterEntry> = {
        ...rest,
        club_id: clubId as string,
      };

      if (user_id && user_id.trim() !== '') {
        payload.user_id = user_id;
      }

      if (mode === 'create') {
        await createMemberRoster(payload).unwrap();
        enqueueSnackbar(
          intl.formatMessage({ id: 'MEMBERS_ROSTER.SUCCESS.CREATED' }),
          { variant: 'success' }
        );
      } else {
        await updateMemberRoster({ id: rosterId!, data: payload }).unwrap();
        enqueueSnackbar(
          intl.formatMessage({ id: 'MEMBERS_ROSTER.SUCCESS.UPDATED' }),
          { variant: 'success' }
        );
      }

      navigate(`/club-management/${clubId}`);
    } catch (error) {
      console.error('Failed to save roster entry:', error);
      enqueueSnackbar(
        intl.formatMessage({ id: 'MEMBERS_ROSTER.ERROR.CREATE_FAILED' }),
        { variant: 'error' }
      );
    }
  };

  if (isLoadingData) {
    return (
      <Container>
        <div className="flex items-center justify-center py-12">
          <KeenIcon
            type="filled"
            icon="spinner"
            className="text-gray-500 text-3xl animate-spin"
          />
          <span className="ml-3 text-gray-600">
            {intl.formatMessage({ id: 'MEMBERS_ROSTER.LOADING' })}
          </span>
        </div>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <div className="flex items-center gap-2">
              <ToolbarPageTitle
                text={intl.formatMessage({
                  id:
                    mode === 'create'
                      ? 'MEMBERS_ROSTER.TITLE.CREATE'
                      : mode === 'edit'
                        ? 'MEMBERS_ROSTER.TITLE.EDIT'
                        : 'MEMBERS_ROSTER.TITLE.VIEW',
                })}
              />
            </div>
            <ToolbarDescription>
              {intl.formatMessage({
                id:
                  mode === 'create'
                    ? 'MEMBERS_ROSTER.DESCRIPTION.CREATE'
                    : mode === 'edit'
                      ? 'MEMBERS_ROSTER.DESCRIPTION.EDIT'
                      : 'MEMBERS_ROSTER.DESCRIPTION.VIEW',
              })}
            </ToolbarDescription>
          </ToolbarHeading>
          <ToolbarActions>
            <div className="form-actions flex items-center gap-3">
              <div className="relative">
                <select
                  id="language-select"
                  className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                  value={currentLanguage.code}
                  onChange={(e) => {
                    const selectedLang = I18N_LANGUAGES.find(
                      (lang) => lang.code === e.target.value
                    );
                    if (selectedLang) {
                      changeLanguage(selectedLang);
                    }
                  }}
                  aria-label={intl.formatMessage({
                    id: 'COMMON.SELECT_LANGUAGE',
                  })}
                >
                  {I18N_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </select>
                <img
                  src="/media/flags/globe.svg"
                  alt="Globe"
                  className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                  aria-hidden="true"
                />
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light dark:btn-light flex items-center gap-2 border border-gray-300 dark:border-coal-100 rounded-lg px-4 py-2 h-10 text-gray-700 dark:text-gray-light-700 hover:bg-gray-100 dark:hover:bg-coal-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleCancel}
              >
                <KeenIcon
                  type="filled"
                  icon="cross"
                  className="text-gray-500 dark:text-gray-light-500"
                />
                {intl.formatMessage({ id: 'MEMBERS_ROSTER.FORM.CANCEL' })}
              </button>
            </div>
          </ToolbarActions>
        </Toolbar>
      </Container>

      <Container>
        <div className="responsive-form">
          <form onSubmit={handleSubmit}>
            <div className="py-4">
              {/* Member Details Section */}
              <div className="pb-6 mb-6">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  {intl.formatMessage({
                    id: 'MEMBERS_ROSTER.SECTION.MEMBER_DETAILS',
                  })}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Member Name */}
                  <div className="md:col-span-2">
                    <label htmlFor="name" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBER_NAME',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      className="input w-full"
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBER_NAME.PLACEHOLDER',
                      })}
                      value={formState.name}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    {errors.name && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.name}
                      </div>
                    )}
                  </div>

                  {/* Member Email */}
                  <div>
                    <label htmlFor="email" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBER_EMAIL',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className={`input w-full ${mode === 'edit' ? 'bg-gray-50 dark:bg-coal-600 cursor-not-allowed' : ''}`}
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBER_EMAIL.PLACEHOLDER',
                      })}
                      value={formState.email}
                      onChange={handleInputChange}
                      disabled={isViewMode || mode === 'edit'}
                    />
                    {errors.email && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.email}
                      </div>
                    )}
                  </div>

                  {/* Member Phone */}
                  <div>
                    <label htmlFor="phone" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBER_PHONE',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      className="input w-full"
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBER_PHONE.PLACEHOLDER',
                      })}
                      value={formState.phone}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    {errors.phone && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.phone}
                      </div>
                    )}
                  </div>

                  {/* Federation Membership Number */}
                  <div>
                    <label
                      htmlFor="federation_membership_number"
                      className="form-label"
                    >
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.FEDERATION_MEMBERSHIP_NUMBER',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="federation_membership_number"
                      name="federation_membership_number"
                      autoComplete="off"
                      type="text"
                      className="input w-full"
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.FEDERATION_MEMBERSHIP_NUMBER.PLACEHOLDER',
                      })}
                      value={formState.federation_membership_number}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    {errors.federation_membership_number && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.federation_membership_number}
                      </div>
                    )}
                  </div>

                  {/* Club Name (Read-only) */}
                  <div>
                    <label htmlFor="club_name" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.CLUB_NAME',
                      })}
                    </label>
                    <input
                      id="club_name"
                      name="club_name"
                      type="text"
                      className="input w-full bg-gray-50 dark:bg-coal-600 dark:text-gray-light-100"
                      value={currentClubName}
                      disabled
                      readOnly
                    />
                  </div>

                  {/* Membership Category */}
                  <div>
                    <label htmlFor="membership_category" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBERSHIP_CATEGORY',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="membership_category"
                      name="membership_category"
                      className="select w-full"
                      value={formState.membership_category}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.MEMBERSHIP_CATEGORY.SELECT',
                        })}
                      </option>
                      {MEMBERSHIP_CATEGORIES.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    {errors.membership_category && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.membership_category}
                      </div>
                    )}
                  </div>

                  {/* SVP Membership ID */}
                  <div>
                    <label htmlFor="svp_membership_id" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.SVP_MEMBERSHIP_ID',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="svp_membership_id"
                      name="svp_membership_id"
                      type="text"
                      autoComplete="off"
                      className="input w-full"
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.SVP_MEMBERSHIP_ID.PLACEHOLDER',
                      })}
                      value={formState.svp_membership_id}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    {errors.svp_membership_id && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.svp_membership_id}
                      </div>
                    )}
                  </div>

                  {/* Membership Start Date */}
                  <div>
                    <label
                      htmlFor="membership_start_date"
                      className="form-label"
                    >
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBERSHIP_START_DATE',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="membership_start_date"
                      name="membership_start_date"
                      type="date"
                      autoComplete="off"
                      className="input w-full"
                      value={formState.membership_start_date}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    {errors.membership_start_date && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.membership_start_date}
                      </div>
                    )}
                  </div>

                  {/* Membership End Date */}
                  <div>
                    <label htmlFor="membership_end_date" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.MEMBERSHIP_END_DATE',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="membership_end_date"
                      name="membership_end_date"
                      type="date"
                      autoComplete="off"
                      className="input w-full"
                      value={formState.membership_end_date}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    {errors.membership_end_date && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.membership_end_date}
                      </div>
                    )}
                  </div>

                  {/* Position of Trust */}
                  <div className="md:col-span-2">
                    <label htmlFor="position_of_trust" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.POSITION_OF_TRUST',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="position_of_trust"
                      name="position_of_trust"
                      className="select w-full"
                      value={formState.position_of_trust}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.POSITION_OF_TRUST.NONE',
                        })}
                      </option>
                      {POSITIONS_OF_TRUST.map((pos) => (
                        <option key={pos.id} value={pos.id}>
                          {intl.formatMessage({
                            id: `MEMBERS_ROSTER.POSITION.${pos.id.toUpperCase()}`,
                          })}
                        </option>
                      ))}
                    </select>
                    {errors.position_of_trust && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.position_of_trust}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* General Billing Details Section */}
              <div className="pb-6 mb-6">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  {intl.formatMessage({
                    id: 'MEMBERS_ROSTER.SECTION.BILLING_DETAILS',
                  })}
                </h2>

                {!isViewMode && (
                  <div className="mb-6 bg-blue-50 dark:bg-blue-900/10 contrast:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-900/20 contrast:border-blue-900/20 flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="use_member_details_for_billing"
                      className="checkbox cursor-pointer"
                      checked={useMemberDetailsForBilling}
                      onChange={handleUseMemberDetailsChange}
                    />
                    <label
                      htmlFor="use_member_details_for_billing"
                      className="text-sm font-medium cursor-pointer text-blue-900 dark:text-blue-400 contrast:text-blue-400 cursor-pointer select-none"
                    >
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.USE_MEMBER_DETAILS_FOR_BILLING',
                      })}
                    </label>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4">
                  {/* Invoice Destination */}
                  <fieldset className="grid grid-cols-1 gap-4">
                    <legend className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.INVOICE_DESTINATION',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </legend>
                    <div className="space-y-2">
                      {INVOICE_DESTINATIONS.map((dest) => (
                        <label
                          key={dest.id}
                          className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-coal-200 p-2 rounded transition-colors"
                        >
                          <input
                            type="radio"
                            name="invoice_destination"
                            value={dest.id}
                            checked={formState.invoice_destination === dest.id}
                            onChange={handleInputChange}
                            className="radio"
                            disabled={isViewMode}
                          />
                          <span className="text-gray-700 dark:text-gray-light-700">
                            {intl.formatMessage({
                              id: `MEMBERS_ROSTER.INVOICE.${dest.id.toUpperCase()}`,
                            })}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.invoice_destination && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.invoice_destination}
                      </div>
                    )}
                  </fieldset>

                  {/* Billing Address Sub-section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {/* Street Address */}
                    <div className="md:col-span-2">
                      <label htmlFor="street_address" className="form-label">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.STREET_ADDRESS',
                        })}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="street_address"
                        name="street_address"
                        type="text"
                        autoComplete="billing address-line1"
                        className="input w-full"
                        placeholder={intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.STREET_ADDRESS.PLACEHOLDER',
                        })}
                        value={formState.street_address}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                      />
                      {errors.street_address && (
                        <div className="text-danger contrast:text-red-500 text-xs mt-1">
                          {errors.street_address}
                        </div>
                      )}
                    </div>

                    {/* Postal Code */}
                    <div>
                      <label htmlFor="postal_code" className="form-label">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.POSTAL_CODE',
                        })}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="postal_code"
                        name="postal_code"
                        type="text"
                        autoComplete="billing postal-code"
                        className="input w-full"
                        placeholder={intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.POSTAL_CODE.PLACEHOLDER',
                        })}
                        value={formState.postal_code}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                      />
                      {errors.postal_code && (
                        <div className="text-danger contrast:text-red-500 text-xs mt-1">
                          {errors.postal_code}
                        </div>
                      )}
                    </div>

                    {/* City */}
                    <div>
                      <label htmlFor="city" className="form-label">
                        {intl.formatMessage({ id: 'MEMBERS_ROSTER.FORM.CITY' })}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="city"
                        name="city"
                        type="text"
                        autoComplete="billing address-level2"
                        className="input w-full"
                        placeholder={intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.CITY.PLACEHOLDER',
                        })}
                        value={formState.city}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                      />
                      {errors.city && (
                        <div className="text-danger contrast:text-red-500 text-xs mt-1">
                          {errors.city}
                        </div>
                      )}
                    </div>

                    {/* Country */}
                    <div>
                      <label htmlFor="country" className="form-label">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.COUNTRY',
                        })}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="country"
                        name="country"
                        type="text"
                        autoComplete="billing country"
                        className="input w-full"
                        placeholder={intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.COUNTRY.PLACEHOLDER',
                        })}
                        value={formState.country}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                      />
                      {errors.country && (
                        <div className="text-danger contrast:text-red-500 text-xs mt-1">
                          {errors.country}
                        </div>
                      )}
                    </div>

                    {/* Billing Name */}
                    <div className="md:col-span-2 mt-2 pt-2 border-t border-gray-100 dark:border-coal-100 italic text-sm text-gray-500 dark:text-gray-light-500">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.SECTION.SPECIFIC_BILLING_DETAILS',
                      })}
                    </div>

                    {/* Billing Name */}
                    <div className="md:col-span-2">
                      <label htmlFor="billing_name" className="form-label">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.BILLING_NAME',
                        })}
                      </label>
                      <input
                        id="billing_name"
                        name="billing_name"
                        type="text"
                        autoComplete="billing name"
                        className="input w-full"
                        placeholder={intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.BILLING_NAME.PLACEHOLDER',
                        })}
                        value={formState.billing_name}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                      />
                    </div>

                    {/* Billing Email */}
                    <div>
                      <label htmlFor="billing_email" className="form-label">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.BILLING_EMAIL',
                        })}
                      </label>
                      <input
                        id="billing_email"
                        name="billing_email"
                        type="email"
                        autoComplete="billing email"
                        className="input w-full"
                        placeholder={intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.BILLING_EMAIL.PLACEHOLDER',
                        })}
                        value={formState.billing_email}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                      />
                      {errors.billing_email && (
                        <div className="text-danger contrast:text-red-500 text-xs mt-1">
                          {errors.billing_email}
                        </div>
                      )}
                    </div>

                    {/* Billing Phone */}
                    <div>
                      <label htmlFor="billing_phone" className="form-label">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.BILLING_PHONE',
                        })}
                      </label>
                      <input
                        id="billing_phone"
                        name="billing_phone"
                        type="tel"
                        autoComplete="billing tel"
                        className="input w-full"
                        placeholder={intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.BILLING_PHONE.PLACEHOLDER',
                        })}
                        value={formState.billing_phone}
                        onChange={handleInputChange}
                        disabled={isViewMode}
                      />
                      {errors.billing_phone && (
                        <div className="text-danger contrast:text-red-500 text-xs mt-1">
                          {errors.billing_phone}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Club-Specific Additional Details Section */}
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  {intl.formatMessage({
                    id: 'MEMBERS_ROSTER.SECTION.ADDITIONAL_DETAILS',
                  })}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Honours/Awards */}
                  <div className="md:col-span-2">
                    <label htmlFor="honours_awards" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.HONOURS_AWARDS',
                      })}
                      <span className="text-gray-500 text-xs ml-2">
                        {intl.formatMessage({
                          id: 'MEMBERS_ROSTER.FORM.HONOURS_AWARDS.HELPER',
                        })}
                      </span>
                    </label>
                    <textarea
                      id="honours_awards"
                      name="honours_awards"
                      className="textarea w-full"
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.HONOURS_AWARDS.PLACEHOLDER',
                      })}
                      rows={3}
                      value={formState.honours_awards}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Storage Locker Key */}
                  <div>
                    <label htmlFor="storage_locker_key" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.STORAGE_LOCKER_KEY',
                      })}
                    </label>
                    <input
                      id="storage_locker_key"
                      name="storage_locker_key"
                      autoComplete="off"
                      type="text"
                      className="input w-full"
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.STORAGE_LOCKER_KEY.PLACEHOLDER',
                      })}
                      value={formState.storage_locker_key}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Eye Color */}
                  <div>
                    <label htmlFor="eye_color" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.EYE_COLOR',
                      })}
                    </label>
                    <input
                      id="eye_color"
                      name="eye_color"
                      autoComplete="off"
                      type="text"
                      className="input w-full"
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.EYE_COLOR.PLACEHOLDER',
                      })}
                      value={formState.eye_color}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                  </div>

                  {/* Shoe Size */}
                  <div>
                    <label htmlFor="shoe_size" className="form-label">
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.SHOE_SIZE',
                      })}
                    </label>
                    <input
                      id="shoe_size"
                      name="shoe_size"
                      autoComplete="off"
                      type="text"
                      className="input w-full"
                      placeholder={intl.formatMessage({
                        id: 'MEMBERS_ROSTER.FORM.SHOE_SIZE.PLACEHOLDER',
                      })}
                      value={formState.shoe_size}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                    />
                    {errors.shoe_size && (
                      <div className="text-danger contrast:text-red-500 text-xs mt-1">
                        {errors.shoe_size}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                type="button"
                className="btn btn-light order-2 sm:order-1"
                onClick={handleCancel}
              >
                {isViewMode
                  ? intl.formatMessage({ id: 'MEMBERS_ROSTER.BUTTON.BACK' })
                  : intl.formatMessage({ id: 'MEMBERS_ROSTER.BUTTON.CANCEL' })}
              </button>
              {!isViewMode && (
                <button
                  type="submit"
                  className="btn btn-primary order-1 sm:order-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <KeenIcon
                        type="filled"
                        icon="spinner"
                        className="animate-spin"
                      />
                      {intl.formatMessage({
                        id: 'MEMBERS_ROSTER.BUTTON.SAVING',
                      })}
                    </>
                  ) : mode === 'create' ? (
                    intl.formatMessage({ id: 'MEMBERS_ROSTER.BUTTON.CREATE' })
                  ) : (
                    intl.formatMessage({ id: 'MEMBERS_ROSTER.BUTTON.UPDATE' })
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </Container>

      {/* Responsive styles */}
      <style>{`
        .responsive-form .card-body {
          padding: 1.5rem;
        }

        @media (max-width: 768px) {
          .responsive-form .card-body {
            padding: 1rem;
          }

          .responsive-form .grid {
            grid-template-columns: 1fr;
          }

          .responsive-form .md\\:col-span-2 {
            grid-column: span 1;
          }
        }

        .responsive-form .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .dark .responsive-form .form-label {
          color: #9A9CAE;
        }

        .contrast .responsive-form .form-label {
          color: #9A9CAE;
        }

        .responsive-form .input,
        .responsive-form .select,
        .responsive-form .textarea {
          font-size: 0.875rem;
        }

        .responsive-form .card-footer {
          padding: 1rem 1.5rem;
          background-color: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .dark .responsive-form .card-footer {
          background-color: #0d0e12;
          border-top-color: #1b1c22;
        }

        .contrast .responsive-form .card-footer {
          background-color: #101010;
          border-top-color: #1b1c22;
        }

        @media (max-width: 640px) {
          .responsive-form .card-footer {
            padding: 1rem;
          }

          .responsive-form .card-footer button {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default MembersRosterForm;
